import { useState, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import { capitalizeFirst, getStatusColor } from '../../utils';
import { TRADE_FILTER_FIELDS } from '../../constants/filters';
import {
  TrendingUp,
  Download,
  DollarSign,
  Activity,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  XCircle,
  Filter,
  RefreshCcw,
} from 'lucide-react';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import { Trade } from '../../Interfaces/mt5Interface';
import SourceCombobox from '../../components/common/SourceCombobox';
import { tradingAccounts } from '../../constants/accountOptions';
import LoaderOverlay from '../../components/common/LoaderOverlay';

const MyTrades = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const filterFields = TRADE_FILTER_FIELDS;
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>(
    tradingAccounts[0]?.label || ''
  );

  const handleExportData = () => {
    console.log('Export trades data');
  };

  const handleCloseTrade = (tradeId: string) => {
    console.log('Close trade:', tradeId);
  };

  const handleViewDetails = (tradeId: string) => {
    console.log('View trade details:', tradeId);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const fetchTrades = useCallback(async () => {
    setIsLoading(true);
    setTrades([]);
    setIsLoading(false);
  }, []);

  const columns: DataTableColumn<Trade>[] = [
    {
      id: 'ticket',
      header: 'Ticket',
      accessor: row => <span className="font-mono text-sm">{row.ticket}</span>,
      sortable: true,
      sortValue: row => row.ticket,
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => <span className="font-semibold">{row.symbol}</span>,
      sortable: true,
      sortValue: row => row.symbol,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge className={getStatusColor(row.type, 'trade')}>
          {row.type === 'BUY' ? (
            <ArrowUpRight className="h-3 w-3 mr-1" />
          ) : (
            <ArrowDownRight className="h-3 w-3 mr-1" />
          )}
          {capitalizeFirst(row.type)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => `${row.volume} lots`,
      sortable: true,
      sortValue: row => row.volume,
    },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: row => row.openPrice.toFixed(5),
      sortable: true,
      sortValue: row => row.openPrice,
    },
    {
      id: 'currentPrice',
      header: 'Current Price',
      accessor: row => (row.currentPrice ? row.currentPrice.toFixed(5) : '-'),
      sortable: true,
      sortValue: row => row.currentPrice || 0,
    },
    {
      id: 'closePrice',
      header: 'Close Price',
      accessor: row => (row.closePrice ? row.closePrice.toFixed(5) : '-'),
      sortable: true,
      sortValue: row => row.closePrice || 0,
    },
    {
      id: 'swap',
      header: 'Swap',
      accessor: row => (
        <span
          className={
            row.swap && row.swap < 0 ? 'text-red-500' : 'text-gray-700'
          }
        >
          ${row.swap ? row.swap.toFixed(2) : '0.00'}
        </span>
      ),
      sortable: true,
      sortValue: row => row.swap || 0,
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <span
          className={`font-semibold ${row.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}
        >
          ${row.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>
          {capitalizeFirst(row.status)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
    },
    {
      id: 'openTime',
      header: 'Open Time',
      accessor: row => {
        let datePart = row.openTime || '';
        let timePart = '';
        if (datePart.includes(' ')) {
          const parts = datePart.split(' ');
          datePart = parts[0];
          timePart = parts[1] || '';
        } else if (datePart.includes('T')) {
          const parts = datePart.split('T');
          datePart = parts[0];
          timePart = parts[1] ? parts[1].split('.')[0] : '';
        }
        return (
          <div className="flex flex-col">
            <span className="text-sm">{datePart}</span>
            {timePart ? (
              <span className="text-xs text-gray-500">{timePart}</span>
            ) : null}
          </div>
        );
      },
      sortable: true,
      sortValue: row => row.openTime,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => handleViewDetails(row.id)}
          additionalItems={
            row.status === 'open' ? (
              <div
                className="flex items-center gap-2 text-sm text-red-600 cursor-pointer px-2 py-2 hover:bg-gray-100"
                onClick={() => handleCloseTrade(row.id)}
              >
                <XCircle className="h-4 w-4" />
                <span>Close Trade</span>
              </div>
            ) : null
          }
        />
      ),
    },
  ];

  const filteredTrades = useMemo(() => {
    return trades.filter(trade => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          trade.ticket.toLowerCase().includes(searchLower) ||
          trade.symbol.toLowerCase().includes(searchLower) ||
          trade.type.toLowerCase().includes(searchLower) ||
          trade.status.toLowerCase().includes(searchLower) ||
          trade.accountNumber.toLowerCase().includes(searchLower) ||
          (trade.comment && trade.comment.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      if (activeFilters.status && trade.status !== activeFilters.status) {
        return false;
      }

      return true;
    });
  }, [trades, searchTerm, activeFilters]);

  const openTrades = filteredTrades.filter((t: Trade) => t.status === 'open');
  const closedTrades = filteredTrades.filter(
    (t: Trade) => t.status === 'closed'
  );
  const totalProfit = filteredTrades.reduce(
    (sum: number, trade: Trade) => sum + trade.profit,
    0
  );
  const totalVolume = filteredTrades.reduce(
    (sum: number, trade: Trade) => sum + trade.volume,
    0
  );
  const todayStr = new Date().toISOString().split('T')[0];
  const closedTodayCount = closedTrades.filter((t: Trade) =>
    t.closeTime ? t.closeTime.includes(todayStr) : false
  ).length;

  return (
    <div className="relative">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="My Trades"
          description="Monitor and manage all your trading activities"
        />
      </div>

      <div className="mb-4">
        <SourceCombobox
          items={tradingAccounts.map(a => a.label)}
          value={selectedAccount}
          onChange={setSelectedAccount}
          placeholder="Select trading account"
        />
      </div>

      <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Open Positions"
          value={openTrades.length}
          change="Active trades"
          icon={Activity}
        />

        <StatsCard
          title="Total P&L"
          value={
            totalProfit >= 0
              ? `+$${totalProfit.toFixed(2)}`
              : `$${totalProfit.toFixed(2)}`
          }
          change="Including commission"
          icon={DollarSign}
        />

        <StatsCard
          title="Closed Today"
          value={closedTodayCount}
          change="Trades closed today"
          icon={Clock}
        />

        <StatsCard
          title="Total Volume"
          value={totalVolume.toFixed(2)}
          change="Lots traded"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>My Trades</CardTitle>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTrades}
                disabled={isLoading}
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportData}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Trades"
          />
          <LoaderOverlay isLoading={isLoading} loadingText="Loading trades...">
            <DataTable
              columns={columns}
              data={filteredTrades}
              searchValue={searchTerm}
              searchPlaceholder="Search by ticket, symbol, type, status, account..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={setActiveFilters}
              showColumnVisibility={true}
              showAddButton={false}
              showExportButton={false}
              initialPageSize={10}
              initialSortBy={{ id: 'openTime', desc: true }}
            />
          </LoaderOverlay>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyTrades;
